import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";

import type { EditorContextParticipant } from "~/stream/NoteCard";

import { renderSnippetToDOM, type Snippet } from "../snippet";
import { blockDescendants } from "../utils";
import { AI_TOPICS_TEMPLATE_ID } from "./aiTopics";
import { getBlockBoundaryState } from "./blockBoundary";
import { collapseChanged, isCollapsed } from "./collapse";
import { getContext, getCurrentUser } from "./context";
import type { ViewDesc } from "./mousePos";
import {
	DUMMY_USER,
	insertSnippetAfterBlockTr,
	promptEnabled,
	promptResponsesPluginKey,
	promptResponseTemplate,
	trackSectionResponse,
} from "./prompt";

import styles from "./prompt.module.css";

const promptPlaceholderPluginKey = new PluginKey<Readonly<{ decorationSet: DecorationSet }>>("prompt-placeholder");

export function makePromptPlaceholderPlugin(
	props: { editorView?: EditorView },
	{ inTemplate = false }: { inTemplate?: boolean } = {},
) {
	const currentUserOverride = inTemplate ? DUMMY_USER : undefined;
	const PLACEHOLDER_ELEMENT_CACHE: { [key: string]: HTMLElement } = {};
	function onClickPlaceholder(event: MouseEvent) {
		const view = props.editorView;
		if (!view) return;
		const target = event.currentTarget as (Node & { pmViewDesc: ViewDesc }) | null;
		const pmViewDesc = target && target.pmViewDesc;
		if (!pmViewDesc) return;
		const pos = pmViewDesc.posAtStart - 1;
		const block = view.state.doc.resolve(pos).node();
		if (!promptEnabled(block)) return;
		const snippet = promptResponseTemplate(block);
		if (!snippet) return;
		let tr = view.state.tr;
		tr = insertSnippetAfterBlockTr(view.state, tr, pos, snippet);
		trackSectionResponse(view.state, block.attrs.blockId, "placeholder");
		view.dispatch(tr);
	}

	function placeholderDecorationToDom(blockId: string, snippet: Snippet, currentUser: EditorContextParticipant) {
		const key = `${snippet.id}${snippet.customTemplateId || ""}|${currentUser.id}|${blockId}`;
		if (key in PLACEHOLDER_ELEMENT_CACHE) return PLACEHOLDER_ELEMENT_CACHE[key];
		const el = document.createElement("div");
		PLACEHOLDER_ELEMENT_CACHE[key] = el;
		el.className = styles.placeholderContainer;
		if (inTemplate) {
			el.classList.add(styles.placeholderContainerInTemplate);
			el.classList.add(styles.readonly);
		}
		renderSnippetToDOM({ snippet, currentUser }, el);
		if (!inTemplate) {
			el.onclick = onClickPlaceholder;
		}
		return el;
	}

	function createPlaceholderDecorationsForTemplate(state: EditorState): DecorationSet {
		const decos: Decoration[] = [];
		const currentUser = currentUserOverride || getCurrentUser(state);
		if (!currentUser) return DecorationSet.empty;
		blockDescendants(state.doc, (node, start) => {
			if (promptEnabled(node)) {
				const snippet = promptResponseTemplate(node);
				if (!snippet || snippet.id === AI_TOPICS_TEMPLATE_ID) return;
				const end = start + node.nodeSize;
				const blockId = node.attrs.blockId;
				decos.push(
					Decoration.widget(end, placeholderDecorationToDom(blockId, snippet, currentUser), {
						side: 2,
						snippetId: snippet.id,
						blockId,
						customTemplateId: snippet.customTemplateId,
					}),
				);
			}
		});
		return DecorationSet.create(state.doc, decos);
	}

	function createPlaceholderDecorations(state: EditorState): DecorationSet {
		if (dontShowPlaceholders(state)) {
			return DecorationSet.empty;
		}
		const decos: Decoration[] = [];
		const blockBoundaryState = getBlockBoundaryState(state);
		const promptResponsesState = promptResponsesPluginKey.getState(state);
		const currentUser = currentUserOverride || getCurrentUser(state);
		if (!promptResponsesState || !currentUser) return DecorationSet.empty;
		for (const [blockId, data] of Object.entries(promptResponsesState)) {
			const blockBoundary = blockBoundaryState?.blockBoundaries[blockId];
			if (!blockBoundary) continue;
			const [_, end, node] = blockBoundary;
			if (node.attrs.promptResponseTemplate?.id === AI_TOPICS_TEMPLATE_ID) continue;
			if (isCollapsed(state, node)) continue;
			const snippet = promptResponseTemplate(node);
			if (!snippet) continue;
			if (data.missing.has(currentUser.id)) {
				decos.push(
					Decoration.widget(end, placeholderDecorationToDom(blockId, snippet, currentUser), {
						side: 2,
						snippetId: snippet.id,
						blockId,
						customTemplateId: snippet.customTemplateId,
					}),
				);
			}
		}
		return DecorationSet.create(state.doc, decos);
	}

	function updatePlaceholderDecorations(tr: Transaction, decoSet: DecorationSet, state: EditorState) {
		if (dontShowPlaceholders(state)) {
			return DecorationSet.empty;
		}
		const oldDecoSet = decoSet.map(tr.mapping, tr.doc);
		const blockBoundaryState = getBlockBoundaryState(state);
		const promptResponsesState = promptResponsesPluginKey.getState(state);
		const currentUser = currentUserOverride || getCurrentUser(state);
		if (!promptResponsesState || !currentUser) return DecorationSet.empty;
		const oldBlockSnippetToDeco: { [key: string]: Decoration } = {};
		oldDecoSet.find().forEach(deco => {
			const {
				spec: { blockId, snippetId, customTemplateId },
			} = deco;
			const key = `${blockId}|${snippetId}${customTemplateId || ""}`;
			oldBlockSnippetToDeco[key] = deco;
		});
		const newBlockSnippetToDeco: { [key: string]: Decoration } = {};
		for (const [blockId, data] of Object.entries(promptResponsesState)) {
			const blockBoundary = blockBoundaryState?.blockBoundaries[blockId];
			if (!blockBoundary) continue;
			const [_, end, node] = blockBoundary;
			if (isCollapsed(state, node) || node.attrs.promptResponseTemplate?.id === AI_TOPICS_TEMPLATE_ID) continue;
			const snippet = promptResponseTemplate(node);
			if (!snippet) continue;
			if (data.missing.has(currentUser.id)) {
				const key = `${blockId}|${snippet.id}${snippet.customTemplateId || ""}`;
				const existingDeco = oldBlockSnippetToDeco[key];
				newBlockSnippetToDeco[key] =
					existingDeco && existingDeco.from === end
						? existingDeco
						: Decoration.widget(end, placeholderDecorationToDom(blockId, snippet, currentUser), {
								side: 2,
								snippetId: snippet.id,
								blockId,
							});
			}
		}
		const newDecos = new Set(Object.values(newBlockSnippetToDeco));
		const oldDecos = new Set(Object.values(oldBlockSnippetToDeco));
		const removedDecos: Decoration[] = [];
		const addedDecos: Decoration[] = [];
		oldDecos.forEach(d => {
			if (!newDecos.has(d)) {
				removedDecos.push(d);
			}
		});
		newDecos.forEach(d => {
			if (!oldDecos.has(d)) {
				addedDecos.push(d);
			}
		});
		return oldDecoSet.remove(removedDecos).add(tr.doc, addedDecos);
	}

	function dontShowPlaceholders(state: EditorState): boolean | undefined {
		const ctx = getContext(state);
		return ctx?.locked || !ctx?.hasAccessToPromptHeadings || ctx.inAgendaBuilderMode;
	}

	function anyPromptHeadingCollapsedChanged(state: EditorState, tr: Transaction): boolean {
		const promptResponsesState = promptResponsesPluginKey.getState(state);
		const blockBoundaryState = getBlockBoundaryState(state);
		if (!promptResponsesState || !blockBoundaryState) return false;

		const { blockBoundaries } = blockBoundaryState;
		for (const blockId of Object.keys(promptResponsesState)) {
			const boundary = blockBoundaries[blockId];
			if (!boundary) continue;
			const [, , headerProseNode] = boundary;
			if (collapseChanged(tr, headerProseNode)) return true;
		}
		return false;
	}

	return new Plugin({
		key: promptPlaceholderPluginKey,
		state: {
			init(_config, state) {
				return {
					decorationSet: inTemplate
						? createPlaceholderDecorationsForTemplate(state)
						: createPlaceholderDecorations(state),
				};
			},
			apply(tr, value, _oldState, newState) {
				if (inTemplate) {
					return {
						decorationSet: createPlaceholderDecorationsForTemplate(newState),
					};
				}
				if (
					!tr.docChanged &&
					!tr.getMeta(promptPlaceholderPluginKey) &&
					!anyPromptHeadingCollapsedChanged(newState, tr)
				)
					return value;
				return { ...value, decorationSet: updatePlaceholderDecorations(tr, value.decorationSet, newState) };
			},
		},
		props: {
			decorations: state => {
				return promptPlaceholderPluginKey.getState(state)?.decorationSet;
			},
		},
		view: view => {
			let prevHidden = dontShowPlaceholders(view.state);
			return {
				update() {
					const newHidden = dontShowPlaceholders(view.state);
					if (newHidden === prevHidden) return;
					prevHidden = newHidden;
					view.dispatch(view.state.tr.setMeta(promptPlaceholderPluginKey, {}));
				},
			};
		},
	});
}
